// src/ui/screens/NotificationsScreen.jsx

import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { NotificationItem } from '../../components/NotificationItem';
import { db } from '../../firebase';
import {
  collection,
  query,
  orderBy,
  onSnapshot,
  doc,
  writeBatch,
  deleteDoc,
  getDoc,
} from 'firebase/firestore';

const COLORS = {
  orange: '#FF8C08',
  bg: '#08090B',
  card: '#23262C',
  cardLight: '#2C2F36',
};

// ── Type map ──────────────────────────────────────────────────────────────────

const TYPE_MAP = {
  'Subscribes': 'subscribe',
  'Likes': 'like',
  'Comments': 'comment',
  'Saves': 'save',
  'New Listings': 'new_listing',
  'Status': 'status',
  'Wants': 'want',
  'Status Likes': 'status_like',
  'Want Likes': 'want_like',
  'Messages': 'message',
  'System': 'system',
};

const TYPE_CHIPS = ['All', ...Object.keys(TYPE_MAP)];

// ── NotificationsScreen ─────────────────────────────────────────────────────

export const NotificationsScreen = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [notifications, setNotifications] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeFilter, setActiveFilter] = useState('All');
  const [showFilterSheet, setShowFilterSheet] = useState(false);
  const [filterSubscribers, setFilterSubscribers] = useState(false);
  const [filterSubscribed, setFilterSubscribed] = useState(false);

  const unsubscribeRef = useRef(null);

  // ── Derived filtered list ───────────────────────────────────────────────
  // Recomputes automatically whenever any of its inputs change — no manual
  // "apply" calls needed, and nothing can ever be called without an argument.

  const filtered = useMemo(() => {
    const uid = user?.uid || '';
    let result = [...notifications];

    // Search
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter((n) => {
        const name = (n.senderName || '').toLowerCase();
        const username = (n.senderUsername || '').toLowerCase();
        return name.includes(q) || username.includes(q);
      });
    }

    // Type chip filter
    if (activeFilter !== 'All') {
      const type = TYPE_MAP[activeFilter] || activeFilter.toLowerCase();
      result = result.filter((n) => n.type === type);
    }

    // Relationship filters
    if (filterSubscribers || filterSubscribed) {
      result = result.filter((n) => {
        const senderSubscribers = n.senderSubscribers || [];
        const senderSubscribed = n.senderSubscribed || [];

        const isSubscriber = senderSubscribed.includes(uid);
        const isSubscribed = senderSubscribers.includes(uid);

        if (filterSubscribers && filterSubscribed) {
          return isSubscriber && isSubscribed;
        } else if (filterSubscribers) {
          return isSubscriber;
        } else {
          return isSubscribed;
        }
      });
    }

    return result;
  }, [
    notifications,
    searchQuery,
    activeFilter,
    filterSubscribers,
    filterSubscribed,
    user?.uid,
  ]);

  // ── Listen to notifications ──────────────────────────────────────────────

  useEffect(() => {
    const uid = user?.uid;
    if (!uid) {
      setIsLoading(false);
      return;
    }

    setIsLoading(true);

    const notificationsRef = collection(
      db,
      'notifications',
      uid,
      'userNotifications'
    );
    const q = query(notificationsRef, orderBy('timestamp', 'desc'));

    const unsubscribe = onSnapshot(
      q,
      async (snapshot) => {
        const enriched = await Promise.all(
          snapshot.docs.map(async (docSnap) => {
            const data = { id: docSnap.id, ...docSnap.data() };

            // Fetch sender profile (system broadcasts carry no senderId)
            const senderId = data.senderId || '';
            if (senderId) {
              try {
                const userSnap = await getDoc(doc(db, 'users', senderId));
                if (userSnap.exists()) {
                  const u = userSnap.data();
                  data.senderName = u.displayName || u.name || 'Unknown';
                  data.senderPhotoURL = u.profileImage || u.photoURL || '';
                  data.senderUsername = u.username || '';
                  data.senderSubscribers = u.subscribers || [];
                  data.senderSubscribed = u.subscribed || [];
                }
              } catch (error) {
                console.error('Enrich sender error:', error);
                data.senderName = 'Unknown';
                data.senderPhotoURL = '';
                data.senderUsername = '';
                data.senderSubscribers = [];
                data.senderSubscribed = [];
              }
            }

            return data;
          })
        );

        setNotifications(enriched);
        setIsLoading(false);

        // Auto-mark all as read
        const unread = snapshot.docs.filter((d) => !d.data().isRead);
        if (unread.length > 0) {
          const batch = writeBatch(db);
          for (const docSnap of unread) {
            const ref = doc(
              db,
              'notifications',
              uid,
              'userNotifications',
              docSnap.id
            );
            batch.update(ref, { isRead: true });
          }
          await batch.commit().catch(() => {});
        }
      },
      (error) => {
        console.error('Notification stream error:', error);
        setIsLoading(false);
      }
    );

    unsubscribeRef.current = unsubscribe;

    return () => {
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
      }
    };
  }, [user?.uid]);

  // ── Delete notification ──────────────────────────────────────────────────

  const deleteNotification = async (id) => {
    const uid = user?.uid;
    if (!uid) return;

    try {
      await deleteDoc(doc(db, 'notifications', uid, 'userNotifications', id));
      setNotifications((prev) => prev.filter((n) => n.id !== id));
    } catch (error) {
      console.error('Delete error:', error);
      alert('Failed to delete notification');
    }
  };

  // ── Clear all ─────────────────────────────────────────────────────────────

  const clearAll = async () => {
    const uid = user?.uid;
    if (!uid) return;

    if (
      !window.confirm(
        'Clear all notifications? This will permanently delete all your notifications.'
      )
    )
      return;

    try {
      const batch = writeBatch(db);
      for (const n of notifications) {
        const ref = doc(db, 'notifications', uid, 'userNotifications', n.id);
        batch.delete(ref);
      }
      await batch.commit();
      setNotifications([]);
    } catch (error) {
      console.error('Clear all error:', error);
      alert('Failed to clear notifications');
    }
  };

  // ── Render ─────────────────────────────────────────────────────────────────

  const hasActiveFilters = filterSubscribers || filterSubscribed;

  const EmptyState = () => (
    <div className="flex flex-col items-center justify-center h-64 gap-4">
      <svg
        className="w-16 h-16 text-white/12"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={1.5}
          d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
        />
      </svg>
      <p className="text-white/35 text-sm font-medium">
        {searchQuery || activeFilter !== 'All'
          ? 'No results for these filters'
          : 'No notifications yet'}
      </p>
      <p className="text-white/20 text-xs">
        {searchQuery || activeFilter !== 'All'
          ? 'Try adjusting your search or filters'
          : 'Your activity will appear here'}
      </p>
    </div>
  );

  const FilterSheet = () => {
    if (!showFilterSheet) return null;

    return (
      <div
        className="p-4 rounded-xl border border-white/10"
        style={{ backgroundColor: COLORS.card }}
      >
        <p className="text-white text-sm font-bold">Filter by relationship</p>
        <div className="space-y-1 mt-2.5">
          <label className="flex items-center gap-2.5 cursor-pointer">
            <input
              type="checkbox"
              checked={filterSubscribers}
              onChange={(e) => setFilterSubscribers(e.target.checked)}
              className="w-4 h-4 rounded accent-filatom-primary"
            />
            <span className="text-white/80 text-sm">
              People subscribed to me
            </span>
          </label>
          <label className="flex items-center gap-2.5 cursor-pointer">
            <input
              type="checkbox"
              checked={filterSubscribed}
              onChange={(e) => setFilterSubscribed(e.target.checked)}
              className="w-4 h-4 rounded accent-filatom-primary"
            />
            <span className="text-white/80 text-sm">
              People I subscribed to
            </span>
          </label>
        </div>
        <div className="flex justify-end gap-2 mt-3">
          <button
            onClick={() => {
              setFilterSubscribers(false);
              setFilterSubscribed(false);
              setShowFilterSheet(false);
            }}
            className="text-white/50 text-sm font-medium"
          >
            Clear
          </button>
          <button
            onClick={() => setShowFilterSheet(false)}
            className="px-4 py-1.5 rounded-full bg-filatom-primary text-black text-sm font-bold"
          >
            Apply
          </button>
        </div>
      </div>
    );
  };

  const SearchBarEl = ({ placeholder }) => (
    <div
      className="h-[46px] rounded-[14px] flex items-center px-3.5"
      style={{ backgroundColor: COLORS.card }}
    >
      <svg
        className="w-5 h-5 text-white/35 flex-shrink-0"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
        />
      </svg>
      <input
        type="text"
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        placeholder={placeholder}
        className="flex-1 h-full bg-transparent text-white text-sm outline-none px-2.5 placeholder:text-white/30"
      />
    </div>
  );

  const TypeChips = () => (
    <div className="flex gap-2 overflow-x-auto py-2.5 hide-scrollbar">
      {TYPE_CHIPS.map((chip) => {
        const active = activeFilter === chip;
        return (
          <button
            key={chip}
            onClick={() => setActiveFilter(chip)}
            className={`px-3.5 py-2 rounded-full text-xs font-medium transition-all whitespace-nowrap ${
              active
                ? 'bg-filatom-primary text-black'
                : 'bg-filatom-card text-white/70'
            }`}
          >
            {chip}
          </button>
        );
      })}
    </div>
  );

  const NotificationsList = () =>
    isLoading ? (
      <div className="flex items-center justify-center h-32">
        <div className="w-8 h-8 border-2 border-filatom-primary border-t-transparent rounded-full animate-spin" />
      </div>
    ) : filtered.length === 0 ? (
      <EmptyState />
    ) : (
      <div className="space-y-0.5">
        {filtered.map((notification) => (
          <NotificationItem
            key={notification.id}
            notification={notification}
            onDelete={() => deleteNotification(notification.id)}
          />
        ))}
      </div>
    );

  return (
    <div className="min-h-screen bg-filatom-bg">
      {/* ══════════════════════════════════════════════════════════════════
          MOBILE LAYOUT (unchanged) — visible below the md breakpoint
         ══════════════════════════════════════════════════════════════════ */}
      <div className="md:hidden">
        {/* Top Bar */}
        <div
          className="fixed top-0 left-0 right-0 z-10 px-4 pt-safe-top bg-filatom-bg/95"
          style={{ paddingTop: 12 }}
        >
          <div className="flex items-center justify-between h-11">
            <button
              onClick={() => navigate(-1)}
              className="w-11 h-11 rounded-full flex items-center justify-center flex-shrink-0"
              style={{
                backgroundColor: 'rgba(35,38,44,0.3)',
                backdropFilter: 'blur(24px)',
                border: '1px solid #23262C',
              }}
            >
              <svg
                className="w-[18px] h-[18px] text-white"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 19l-7-7 7-7"
                />
              </svg>
            </button>
            <span className="text-white text-xl font-bold">Activity</span>
            <div
              className="flex items-center rounded-full flex-shrink-0"
              style={{
                backgroundColor: 'rgba(35,38,44,0.3)',
                backdropFilter: 'blur(24px)',
                border: '1px solid #23262C',
              }}
            >
              <button
                onClick={() => setShowFilterSheet(!showFilterSheet)}
                className="w-11 h-11 rounded-full flex items-center justify-center"
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  style={{
                    color: hasActiveFilters ? COLORS.orange : '#FFFFFF',
                  }}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z"
                  />
                </svg>
              </button>
              {notifications.length > 0 && (
                <>
                  <div className="w-px h-5 bg-white/10" />
                  <button
                    onClick={clearAll}
                    className="w-11 h-11 rounded-full flex items-center justify-center"
                  >
                    <svg
                      className="w-5 h-5 text-white"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                      />
                    </svg>
                  </button>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="pt-[72px] px-4 pb-20">
          <SearchBarEl placeholder="Search by name or username…" />
          <TypeChips />
          {showFilterSheet && (
            <div className="my-2">
              <FilterSheet />
            </div>
          )}
          <NotificationsList />
        </div>
      </div>

      {/* ══════════════════════════════════════════════════════════════════
          DESKTOP LAYOUT (unchanged) — visible at md breakpoint and up.
          Sticky header with the filter/clear actions inline, then a
          centered single column — an activity feed reads best as one
          focused column, not a grid.
         ══════════════════════════════════════════════════════════════════ */}
      <div className="hidden md:block">
        <div
          className="sticky top-0 z-10 backdrop-blur-sm border-b border-white/10"
          style={{ backgroundColor: `${COLORS.bg}E6` }}
        >
          <div className="max-w-3xl mx-auto px-8 h-20 flex items-center gap-5">
            <button
              onClick={() => navigate(-1)}
              className="w-10 h-10 rounded-full flex items-center justify-center border border-white/10 hover:bg-white/10 transition-colors"
              style={{ backgroundColor: COLORS.card }}
              aria-label="Go back"
            >
              <svg
                className="w-4 h-4 text-white"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 19l-7-7 7-7"
                />
              </svg>
            </button>

            <div className="flex-1">
              <h1 className="text-white text-2xl font-bold leading-tight">
                Activity
              </h1>
              <p className="text-white/40 text-sm mt-0.5">
                Subscribes, likes, comments and more
              </p>
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowFilterSheet(!showFilterSheet)}
                className="w-10 h-10 rounded-full flex items-center justify-center border border-white/10 hover:bg-white/10 transition-colors"
                style={{ backgroundColor: COLORS.card }}
                aria-label="Filter"
              >
                <svg
                  className="w-4.5 h-4.5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  style={{
                    color: hasActiveFilters ? COLORS.orange : '#FFFFFF',
                  }}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z"
                  />
                </svg>
              </button>
              {notifications.length > 0 && (
                <button
                  onClick={clearAll}
                  className="w-10 h-10 rounded-full flex items-center justify-center border border-white/10 hover:bg-white/10 transition-colors"
                  style={{ backgroundColor: COLORS.card }}
                  aria-label="Clear all"
                >
                  <svg
                    className="w-4.5 h-4.5 text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                    />
                  </svg>
                </button>
              )}
            </div>
          </div>
        </div>

        <div className="max-w-3xl mx-auto px-8 py-8">
          <SearchBarEl placeholder="Search by name or username…" />
          <TypeChips />
          {showFilterSheet && (
            <div className="mb-4">
              <FilterSheet />
            </div>
          )}
          <NotificationsList />
        </div>
      </div>

      <style>{`
        .hide-scrollbar::-webkit-scrollbar {
          display: none;
        }
        .hide-scrollbar {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
      `}</style>
    </div>
  );
};

export default NotificationsScreen;